"use client"

import { useState, useEffect, useRef } from "react"
import { useTranslation } from "../../hooks/useTranslation"
import { api } from "../../services/api"
import { toast } from "react-toastify"
import { format } from "date-fns"
import { fr, enUS } from "date-fns/locale"
import { FiUser, FiShield, FiSettings, FiLogOut, FiCamera } from "react-icons/fi"

// Import des composants de section
import { ProfileSection } from "./profile/ProfileSection"
import { SecuritySection } from "./profile/SecuritySection"
import { PreferencesSection } from "./profile/PreferencesSection"
import { SettingsSection } from "./profile/SettingsSection"
import { Image } from 'antd';

const languageMap = {
  fr: fr,
  en: enUS,
}

const ProfileCard = ({ title, icon: Icon, children, className = "" }) => (
  <div
    className={`bg-gray-800/50 backdrop-blur-sm border border-amber-500/20 rounded-2xl shadow-lg p-6 transition-all duration-300 hover:border-amber-500/40 hover:shadow-amber-500/10 hover:shadow-xl ${className}`}
  >
    <div className="flex items-center mb-4">
      <div className="p-3 bg-gradient-to-br from-amber-500/20 to-amber-600/20 rounded-xl text-amber-400 mr-3 shadow-inner">
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="text-lg font-semibold text-white">{title}</h3>
    </div>
    <div className="text-gray-300">{children}</div>
  </div>
)

const StatCard = ({ value, label, icon: Icon, color = "amber" }) => {
  const colorMap = {
    amber: {
      bg: "bg-gradient-to-br from-amber-500/20 to-amber-600/20",
      text: "text-amber-400",
      value: "text-white",
      label: "text-gray-400",
      border: "border-amber-500/30",
    },
    blue: {
      bg: "bg-gradient-to-br from-blue-500/20 to-blue-600/20",
      text: "text-blue-400",
      value: "text-white",
      label: "text-gray-400",
      border: "border-blue-500/30",
    },
    green: {
      bg: "bg-gradient-to-br from-green-500/20 to-green-600/20",
      text: "text-green-400",
      value: "text-white",
      label: "text-gray-400",
      border: "border-green-500/30",
    },
    red: {
      bg: "bg-gradient-to-br from-red-500/20 to-red-600/20",
      text: "text-red-400",
      value: "text-white",
      label: "text-gray-400",
      border: "border-red-500/30",
    },
  }

  const colors = colorMap[color] || colorMap.amber

  return (
    <div
      className={`bg-gray-800/50 backdrop-blur-sm border ${colors.border} rounded-2xl shadow-lg p-4 hover:shadow-xl transition-all duration-300`}
    >
      <div className="flex items-center">
        <div className={`p-3 rounded-xl ${colors.bg} ${colors.text} mr-3 shadow-inner`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className={`text-2xl font-bold ${colors.value}`}>{value}</p>
          <p className={`text-sm ${colors.label}`}>{label}</p>
        </div>
      </div>
    </div>
  )
}

const Profile = () => {
  const { t: translation, language: currentLanguage } = useTranslation()
  const [activeTab, setActiveTab] = useState("profile")
  const [user, setUser] = useState(JSON.parse(localStorage.getItem("user") || "{}"))
  const [form, setForm] = useState({
    phone: user.phone || "",
    address: user.address || "",
    preferences: user.preferences || {
      notifications: true,
      newsletter: false,
      darkMode: false,
      language: "fr",
    },
  })

  const [stats, setStats] = useState({
    totalMoves: 0,
    upcomingMoves: 0,
    totalSpent: 0,
    moveTypes: {},
  })

  const [editing, setEditing] = useState(false)
  const [avatar, setAvatar] = useState(null)
  const [avatarPreview, setAvatarPreview] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [recentMoves, setRecentMoves] = useState([])
  const [is2FAEnabled, setIs2FAEnabled] = useState(false)
  const [is2FALoading, setIs2FALoading] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const fileInputRef = useRef(null)

  // Vérifier l'authentification au chargement du composant
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem("token")
      const storedUser = localStorage.getItem("user")

      if (!token) {
        console.error("Aucun token d'authentification trouvé")
        window.location.href = "/login"
        return false
      }

      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser)
          setUser(userData)
          setForm((prev) => ({
            ...prev,
            phone: userData.phone || "",
            address: userData.address || "",
            preferences: userData.preferences || prev.preferences,
          }))
          return true
        } catch (e) {
          console.error("Erreur lors du parsing des données utilisateur:", e)
        }
      }

      return true
    }

    // Si l'authentification est valide, charger les données
    if (checkAuth()) {
      const fetchUserData = async () => {
        try {
          console.log("Début du chargement des données du profil...")
          setLoading(true)

          // Récupérer les données en parallèle
          const [userResponse, statsResponse, movesResponse] = await Promise.all([
            api.getUserProfile(),
            api.getUserStats().catch((e) => {
              console.error("Erreur lors de la récupération des statistiques:", e)
              return { data: { totalMoves: 0, upcomingMoves: 0, totalSpent: 0, moveTypes: {} } }
            }),
            api.getClientMoves
              ? api.getClientMoves().catch((e) => {
                  console.error("Erreur lors de la récupération des déménagements:", e)
                  return { data: [] }
                })
              : { data: [] },
          ])

          console.log("Réponse de getUserProfile:", userResponse)

          // Mettre à jour l'état utilisateur avec les données du profil
          setUser((prevUser) => ({
            ...prevUser,
            ...userResponse,
            name: [userResponse.firstName, userResponse.lastName].filter(Boolean).join(" ") || "Utilisateur",
          }))
          // Mettre à jour le formulaire avec les données du profil
          setForm((prev) => ({
            ...prev,
            phone: userResponse.phone || "",
            address: userResponse.address || "",
            preferences: userResponse.preferences || prev.preferences,
          }))

          console.log("Données du profil mises à jour:", userResponse)

          // Mettre à jour les statistiques et les déménagements récents
          if (statsResponse && statsResponse.data) {
            setStats(statsResponse.data)
            console.log("Statistiques mises à jour:", statsResponse.data)
          }

          if (movesResponse && movesResponse.data) {
            setRecentMoves(movesResponse.data)
            console.log("Déménagements récents:", movesResponse.data)
          }

          // Vérifier si la 2FA est activée
          const twoFactorEnabled =
            userResponse.twoFactorEnabled || userResponse.mfa_enabled || userResponse.mfaEnabled || false
          setIs2FAEnabled(twoFactorEnabled)
          console.log("2FA activée:", twoFactorEnabled)
        } catch (error) {
          console.error("Erreur lors de la récupération des données utilisateur:", error)
          toast.error(translation.errorLoadingProfile || "Erreur lors du chargement du profil")

          // Si l'erreur est liée à l'authentification, rediriger vers la page de connexion
          if (error.message && (error.message.includes("401") || error.message.includes("token"))) {
            console.log("Redirection vers la page de connexion...")
            localStorage.removeItem("token")
            localStorage.removeItem("user")
            window.location.href = "/login"
          }
        } finally {
          setLoading(false)
          console.log("Chargement des données terminé")
        }
      }

      fetchUserData()
    }
  }, [translation])

  // Gestion des changements de formulaire
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  // Gestion du changement de fichier (avatar)
  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setAvatar(file)
      setAvatarPreview(URL.createObjectURL(file))
    }
  }

  // Sauvegarde du profil
  const handleSaveProfile = async () => {
    setSaving(true)
    try {
      // Préparer les données à envoyer
      const payload = {
        phone: form.phone,
        address: form.address,
        preferences: form.preferences,
      }

      // Si un nouvel avatar est sélectionné, l'uploader d'abord
      if (avatar) {
        try {
          const formData = new FormData()
          formData.append("avatar", avatar)
          const uploadResponse = await api.uploadAvatar(formData)

          if (uploadResponse && uploadResponse.avatarUrl) {
            // Mettre à jour l'URL de l'avatar dans les données du profil
            payload.avatar = uploadResponse.avatarUrl
          }
        } catch (uploadError) {
          console.error("Error uploading avatar:", uploadError)
          toast.error(translation.errorUploadingAvatar || "Error uploading avatar")
          return
        }
      }

      // Mettre à jour le profil
      const response = await api.updateProfile(payload)

      // Mise à jour de l'utilisateur dans le state et le localStorage
      const updatedUser = {
        ...user,
        ...response.data,
        // Mettre à jour l'avatar s'il a été modifié
        ...(payload.avatar && { avatar: payload.avatar }),
      }

      setUser(updatedUser)
      localStorage.setItem("user", JSON.stringify(updatedUser))

      // Réinitialiser l'état de l'avatar
      setAvatar(null)
      setAvatarPreview(null)

      toast.success(translation.profileUpdated || "Profile updated successfully")
      setEditing(false)
    } catch (error) {
      console.error("Error updating profile:", error)
      const errorMessage = error.response?.data?.message || error.message || "Error updating profile"
      toast.error(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  // Changement de mot de passe
  const handlePasswordChange = async ({ currentPassword, newPassword, confirmPassword }) => {
    try {
      // Validation côté client
      if (!currentPassword || !newPassword || !confirmPassword) {
        throw new Error(translation.allFieldsRequired || "Tous les champs sont obligatoires")
      }

      if (newPassword.length < 8) {
        throw new Error(translation.passwordTooShort || "Le mot de passe doit contenir au moins 8 caractères")
      }

      if (newPassword !== confirmPassword) {
        throw new Error(translation.passwordsDontMatch || "Les mots de passe ne correspondent pas")
      }

      // Appel à l'API pour changer le mot de passe
      const response = await api.changePassword({
        currentPassword,
        newPassword,
        confirmPassword: newPassword, // Certaines API peuvent exiger la confirmation
      })

      // Vérifier si la réponse contient un message de succès
      if (response && response.success === false) {
        throw new Error(
          response.message || translation.errorUpdatingPassword || "Erreur lors de la mise à jour du mot de passe",
        )
      }

      // Afficher un message de succès
      toast.success(translation.passwordUpdated || "Mot de passe mis à jour avec succès")

      // Réinitialiser le formulaire si nécessaire
      return true
    } catch (error) {
      console.error("Erreur lors du changement de mot de passe:", error)

      // Gérer les erreurs spécifiques
      let errorMessage =
        error.message || translation.errorUpdatingPassword || "Erreur lors de la mise à jour du mot de passe"

      // Traduire les messages d'erreur courants
      if (error.message && error.message.includes("current password")) {
        errorMessage = translation.incorrectCurrentPassword || "Le mot de passe actuel est incorrect"
      } else if (error.message && error.message.includes("same as old")) {
        errorMessage = translation.sameAsOldPassword || "Le nouveau mot de passe doit être différent de l'ancien"
      }

      // Afficher le message d'erreur
      toast.error(errorMessage)

      // Propager l'erreur pour une gestion supplémentaire si nécessaire
      throw error
    }
  }

  // Configuration de l'authentification à deux facteurs (2FA)
  const setup2FA = async () => {
    try {
      setIs2FALoading(true)

      // Vérifier si la 2FA est déjà activée
      if (is2FAEnabled) {
        // Si déjà activée, demander confirmation avant désactivation
        if (
          window.confirm(
            translation.confirmDisable2FA || "Êtes-vous sûr de vouloir désactiver l'authentification à deux facteurs ?",
          )
        ) {
          await disable2FA()
        }
        return
      }

      // Sinon, procéder à l'activation
      const response = await api.setup2FA()

      if (response && response.data) {
        // Afficher le QR code et demander le code de vérification
        const verificationCode = window.prompt(
          `${translation.scanQRCode || "Scannez le QR code avec votre application d'authentification (Google Authenticator, Authy, etc.) et entrez le code à 6 chiffres généré"}:\n\n${response.data.qrCodeUrl ? `QR Code URL: ${response.data.qrCodeUrl}` : ""}`,
          "",
        )

        if (verificationCode) {
          // Vérifier le code saisi
          const verifyResponse = await api.verify2FACode(verificationCode)

          if (verifyResponse && verifyResponse.success) {
            // Sauvegarder les codes de récupération
            if (verifyResponse.recoveryCodes) {
              const recoveryCodesText = verifyResponse.recoveryCodes.join("\n")
              const saveCodes = window.confirm(
                `${translation.saveRecoveryCodes || "Veuillez sauvegarder ces codes de récupération dans un endroit sûr. Ils ne seront affichés qu'une seule fois."}:\n\n${recoveryCodesText}\n\n${translation.copyToClipboard || "Copier dans le presse-papiers ?"}`,
              )

              if (saveCodes) {
                try {
                  await navigator.clipboard.writeText(recoveryCodesText)
                  toast.success(translation.codesCopied || "Codes de récupération copiés dans le presse-papiers")
                } catch (err) {
                  console.error("Erreur lors de la copie dans le presse-papiers:", err)
                }
              }
            }

            // Mettre à jour l'état 2FA
            setIs2FAEnabled(true)
            toast.success(translation.twoFAEnabled || "Authentification à deux facteurs activée avec succès")
          } else {
            throw new Error(translation.invalid2FACode || "Code de vérification invalide")
          }
        } else {
          throw new Error(translation.noVerificationCode || "Aucun code de vérification fourni")
        }
      }
    } catch (error) {
      console.error("Erreur lors de la configuration de la 2FA:", error)
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        translation.errorSettingUp2FA ||
        "Erreur lors de la configuration de l'authentification à deux facteurs"
      toast.error(errorMessage)

      // En cas d'échec, s'assurer que la 2FA est désactivée
      if (is2FAEnabled) {
        try {
          await api.disable2FA()
        } catch (disableError) {
          console.error("Erreur lors de la désactivation de la 2FA après échec:", disableError)
        }
      }
      setIs2FAEnabled(false)
    } finally {
      setIs2FALoading(false)
    }
  }

  // Désactivation de l'authentification à deux facteurs (2FA)
  const disable2FA = async (showConfirmation = true) => {
    try {
      // Demander confirmation à l'utilisateur
      if (showConfirmation) {
        const confirmDisable = window.confirm(
          translation.confirmDisable2FA ||
            "Êtes-vous sûr de vouloir désactiver l'authentification à deux facteurs ? Votre compte sera moins sécurisé.",
        )

        if (!confirmDisable) {
          return false // L'utilisateur a annulé
        }
      }

      setIs2FALoading(true)

      // Appeler l'API pour désactiver la 2FA
      const response = await api.disable2FA()

      // Vérifier la réponse de l'API
      if (response && response.success === false) {
        throw new Error(
          response.message ||
            translation.errorDisabling2FA ||
            "Erreur lors de la désactivation de l'authentification à deux facteurs",
        )
      }

      // Mettre à jour l'état local
      setIs2FAEnabled(false)

      // Mettre à jour les données utilisateur dans le localStorage
      const updatedUser = { ...user, twoFactorEnabled: false }
      setUser(updatedUser)
      localStorage.setItem("user", JSON.stringify(updatedUser))

      // Afficher un message de succès
      toast.success(translation.twoFADisabled || "Authentification à deux facteurs désactivée avec succès")

      return true
    } catch (error) {
      console.error("Erreur lors de la désactivation de la 2FA:", error)

      // Gérer les erreurs spécifiques
      const errorMessage =
        error.message ||
        translation.errorDisabling2FA ||
        "Erreur lors de la désactivation de l'authentification à deux facteurs"

      // Afficher le message d'erreur
      toast.error(errorMessage)

      // Propager l'erreur pour une gestion supplémentaire si nécessaire
      throw error
    } finally {
      setIs2FALoading(false)
    }
  }

  // Export des données utilisateur
  const handleExportData = async () => {
    try {
      // Demander confirmation à l'utilisateur
      const confirmExport = window.confirm(
        translation.confirmDataExport ||
          "Voulez-vous exporter toutes vos données personnelles ? Cela peut prendre quelques instants.",
      )

      if (!confirmExport) {
        return // L'utilisateur a annulé
      }

      setIsExporting(true)

      // Afficher un message de chargement
      toast.info(translation.preparingDataExport || "Préparation de l'export de vos données...")

      // Appeler l'API pour exporter les données
      const response = await api.exportUserData()

      // Vérifier la réponse de l'API
      if (!response || !response.data) {
        throw new Error(translation.noDataToExport || "Aucune donnée à exporter")
      }

      // Formater les données pour l'export
      const exportData = typeof response.data === "string" ? response.data : JSON.stringify(response.data, null, 2)

      // Créer un blob avec les données
      const blob = new Blob([exportData], {
        type: "application/json;charset=utf-8",
      })

      // Créer un lien de téléchargement
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url

      // Nom du fichier avec la date
      const dateStr = new Date().toISOString().split("T")[0]
      link.download = `liberty-export-${dateStr}.json`

      // Déclencher le téléchargement
      document.body.appendChild(link)
      link.click()

      // Nettoyer
      setTimeout(() => {
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      }, 0)

      // Afficher un message de succès
      toast.success(translation.dataExported || "Vos données ont été exportées avec succès")
    } catch (error) {
      console.error("Erreur lors de l'export des données:", error)

      // Gérer les erreurs spécifiques
      let errorMessage = error.message || translation.errorExportingData || "Erreur lors de l'export des données"

      // Traduire les messages d'erreur courants
      if (error.message && error.message.includes("401")) {
        errorMessage = translation.unauthorizedExport || "Vous devez être connecté pour exporter vos données"
      } else if (error.message && error.message.includes("rate limit")) {
        errorMessage =
          translation.exportRateLimit || "Vous avez dépassé la limite d'export. Veuillez réessayer plus tard."
      }

      // Afficher le message d'erreur
      toast.error(errorMessage)
    } finally {
      setIsExporting(false)
    }
  }

  // Suppression du compte utilisateur
  const handleDeleteAccount = async () => {
    try {
      // Demander confirmation avec un message clair
      const confirmText =
        translation.enterDeleteConfirmation ||
        'Pour confirmer la suppression de votre compte, veuillez taper "SUPPRIMER MON COMPTE"'

      const userInput = window.prompt(confirmText)

      if (userInput !== "SUPPRIMER MON COMPTE") {
        if (userInput !== null) {
          // L'utilisateur a annulé ou saisi autre chose
          toast.info(translation.accountDeletionCancelled || "Suppression du compte annulée")
        }
        return
      }

      // Demander le mot de passe pour confirmation finale
      const password = window.prompt(
        translation.enterPasswordToConfirmDeletion ||
          "Pour des raisons de sécurité, veuillez entrer votre mot de passe pour confirmer la suppression de votre compte :",
      )

      if (!password) {
        toast.info(translation.accountDeletionCancelled || "Suppression du compte annulée")
        return
      }

      setIsDeleting(true)

      // Afficher un message de chargement
      toast.info(translation.deletingAccount || "Suppression de votre compte en cours...")

      // Appeler l'API pour supprimer le compte
      const response = await api.deleteAccount({ password })

      // Vérifier la réponse de l'API
      if (response && response.success === false) {
        throw new Error(
          response.message || translation.errorDeletingAccount || "Erreur lors de la suppression du compte",
        )
      }

      // Nettoyer les données locales
      localStorage.removeItem("token")
      localStorage.removeItem("user")

      // Afficher un message de succès
      toast.success(
        translation.accountDeletedSuccessfully ||
          "Votre compte a été supprimé avec succès. Nous sommes désolés de vous voir partir.",
      )

      // Rediriger vers la page d'accueil ou de connexion
      setTimeout(() => {
        window.location.href = "/"
      }, 2000)
    } catch (error) {
      console.error("Erreur lors de la suppression du compte:", error)

      // Gérer les erreurs spécifiques
      let errorMessage = error.message || translation.errorDeletingAccount || "Erreur lors de la suppression du compte"

      // Traduire les messages d'erreur courants
      if (error.message && error.message.includes("password")) {
        errorMessage = translation.incorrectPassword || "Mot de passe incorrect"
      } else if (error.message && error.message.includes("401")) {
        errorMessage = translation.unauthorizedAction || "Non autorisé. Veuillez vous reconnecter."
      }

      // Afficher le message d'erreur
      toast.error(errorMessage)
    } finally {
      setIsDeleting(false)
    }
  }

  // Sauvegarde des préférences utilisateur
  const handleSavePreferences = async (newPreferences) => {
    try {
      // Valider les préférences
      if (!newPreferences || typeof newPreferences !== "object") {
        throw new Error(translation.invalidPreferences || "Préférences invalides")
      }

      setSaving(true)

      // Afficher un message de chargement
      toast.info(translation.savingPreferences || "Enregistrement de vos préférences...")

      // Appeler l'API pour mettre à jour les préférences
      const response = await api.updatePreferences(newPreferences)

      // Vérifier la réponse de l'API
      if (!response || !response.data) {
        throw new Error(translation.errorSavingPreferences || "Erreur lors de l'enregistrement des préférences")
      }

      // Mettre à jour l'utilisateur dans le state et le localStorage
      const updatedUser = {
        ...user,
        preferences: { ...user.preferences, ...response.data },
      }

      setUser(updatedUser)
      setForm((prev) => ({
        ...prev,
        preferences: { ...prev.preferences, ...response.data },
      }))

      // Mettre à jour le localStorage
      localStorage.setItem("user", JSON.stringify(updatedUser))

      // Vérifier si la langue ou le thème a changé
      const languageChanged = newPreferences.language && newPreferences.language !== currentLanguage
      const themeChanged = newPreferences.theme && newPreferences.theme !== (user.preferences?.theme || "light")

      // Afficher un message de succès
      toast.success(translation.preferencesSaved || "Préférences enregistrées avec succès")

      // Si la langue ou le thème a changé, recharger la page
      if (languageChanged || themeChanged) {
        // Donner le temps au toast de s'afficher avant le rechargement
        setTimeout(() => {
          window.location.reload()
        }, 1000)
      }

      return true
    } catch (error) {
      console.error("Erreur lors de l'enregistrement des préférences:", error)

      // Gérer les erreurs spécifiques
      const errorMessage =
        error.message || translation.errorSavingPreferences || "Erreur lors de l'enregistrement des préférences"

      // Afficher le message d'erreur
      toast.error(errorMessage)

      return false
    } finally {
      setSaving(false)
    }
  }

  // Composant pour afficher un document
  const DocumentCard = ({ title, url }) => {
    if (!url) return null;
    
    return (
      <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700">
        <h3 className="text-amber-400 font-medium mb-2">{title}</h3>
        <div className="relative aspect-[3/2] bg-gray-700/50 rounded-md overflow-hidden">
          <Image
            src={url}
            alt={title}
            className="object-cover w-full h-full"
            preview={{
              mask: 'Voir en grand',
              maskClassName: 'rounded-md'
            }}
          />
        </div>
      </div>
    );
  };

  // Navigation
  const renderTabContent = () => {
    switch (activeTab) {
      case "profile":
        return (
          <div>
            <ProfileSection
              user={user}
              form={form}
              editing={editing}
              avatarPreview={avatarPreview}
              fileInputRef={fileInputRef}
              onInputChange={handleInputChange}
              onFileChange={handleFileChange}
              onSave={handleSaveProfile}
              onEditToggle={() => setEditing(!editing)}
            />
            
            {/* Section des documents du déménageur */}
            {user.role === 'demenageur' && (
              <div className="mt-8">
                <h2 className="text-xl font-semibold text-white mb-4">Mes documents</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <DocumentCard 
                    title="Carte d'identité" 
                    url={user.cin_photo_url} 
                  />
                  <DocumentCard 
                    title="Attestation d'assurance" 
                    url={user.insurance_photo_url} 
                  />
                  <DocumentCard 
                    title="Carte grise" 
                    url={user.registration_photo_url} 
                  />
                </div>
              </div>
            )}
          </div>
        )
      case "security":
        return (
          <SecuritySection
            onPasswordChange={handlePasswordChange}
            on2FASetup={setup2FA}
            on2FADisable={disable2FA}
            is2FAEnabled={is2FAEnabled}
            is2FALoading={is2FALoading}
          />
        )
      case "preferences":
        return (
          <PreferencesSection
            preferences={form.preferences}
            onPreferencesChange={handleSavePreferences}
            onSavePreferences={handleSavePreferences}
          />
        )
      case "settings":
        return (
          <SettingsSection
            user={user}
            onExportData={handleExportData}
            onDeleteAccount={handleDeleteAccount}
            isExporting={isExporting}
            isDeleting={isDeleting}
          />
        )
      default:
        return null
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="relative">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-amber-500"></div>
          <div className="absolute inset-0 animate-ping rounded-full h-16 w-16 border border-amber-500/30"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Sidebar */}
          <div className="w-full md:w-64 flex-shrink-0">
            <div className="bg-gray-800/50 backdrop-blur-sm border border-amber-500/20 rounded-2xl shadow-lg overflow-hidden">
              <div className="p-6 text-center">
                <div className="relative mx-auto w-32 h-32 rounded-full overflow-hidden border-4 border-amber-500/30 mb-4 shadow-lg">
                  <img
                    src={
                      avatarPreview ||
                      user.avatar ||
                      `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=f59e0b&color=1f2937`
                    }
                    alt={user.name}
                    className="w-full h-full object-cover"
                  />
                  {editing && (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 hover:opacity-100 transition-all duration-300 backdrop-blur-sm"
                    >
                      <FiCamera className="h-6 w-6 text-amber-400" />
                    </button>
                  )}
                </div>
                <h2 className="text-xl font-bold text-white">{user.name}</h2>
                <p className="text-sm text-gray-400">{user.email}</p>

                <div className="mt-4 flex justify-center space-x-2">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-amber-500/20 text-amber-400 border border-amber-500/30">
                    {user.role || "Client"}
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
                    {user.status || "Active"}
                  </span>
                </div>
              </div>

              <nav className="space-y-1 p-2">
                {[
                  { id: "profile", icon: FiUser, label: "Profil" },
                  { id: "security", icon: FiShield, label: "Sécurité" },
                  { id: "preferences", icon: FiSettings, label: "Préférences" },
                  { id: "settings", icon: FiSettings, label: "Paramètres" },
                ].map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-all duration-300 ${
                      activeTab === item.id
                        ? "bg-amber-500/20 text-amber-400 border border-amber-500/30 shadow-lg"
                        : "text-gray-300 hover:bg-gray-700/50 hover:text-amber-400 border border-transparent hover:border-red-500/30"
                    }`}
                  >
                    <item.icon className="mr-3 h-5 w-5" />
                    {item.label}
                  </button>
                ))}
              </nav>

              <div className="p-4 border-t border-amber-500/20">
                <button
                  onClick={() => {
                    localStorage.removeItem("token")
                    localStorage.removeItem("user")
                    window.location.href = "/login"
                  }}
                  className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-red-400 hover:text-red-300 rounded-lg hover:bg-red-500/20 border border-transparent hover:border-red-500/30 transition-all duration-300"
                >
                  <FiLogOut className="mr-2 h-4 w-4" />
                  Déconnexion
                </button>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <div className="bg-gray-800/50 backdrop-blur-sm border border-amber-500/20 rounded-2xl shadow-lg overflow-hidden">
              {/* Tab Content */}
              <div className="p-6">{renderTabContent()}</div>
            </div>

            {/* Recent Moves */}
            {activeTab === "profile" && recentMoves.length > 0 && (
              <div className="mt-6 bg-gray-800/50 backdrop-blur-sm border border-amber-500/20 rounded-2xl shadow-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-amber-500/20">
                  <h3 className="text-lg font-medium text-white">Déménagements récents</h3>
                </div>
                <div className="divide-y divide-amber-500/10">
                  {recentMoves.slice(0, 3).map((move) => (
                    <div key={move.id} className="p-4 hover:bg-gray-700/30 transition-all duration-300">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-white">Déménagement #{move.reference}</h4>
                          <p className="text-sm text-gray-400 mt-1">
                            {format(new Date(move.move_date), "PPP", {
                              locale: languageMap[form.preferences?.language || "fr"],
                            })}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-amber-400">
                            {move.price_cents ? `${(move.price_cents / 100).toFixed(2)} €` : "Devis en cours"}
                          </p>
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                              move.status === "completed"
                                ? "bg-green-500/20 text-green-400 border-green-500/30"
                                : move.status === "in_progress"
                                  ? "bg-blue-500/20 text-blue-400 border-blue-500/30"
                                  : "bg-amber-500/20 text-amber-400 border-amber-500/30"
                            }`}
                          >
                            {move.status === "completed"
                              ? "Terminé"
                              : move.status === "in_progress"
                                ? "En cours"
                                : "En attente"}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {recentMoves.length > 3 && (
                  <div className="px-6 py-3 bg-gray-800/30 text-right border-t border-amber-500/20">
                    <button
                      onClick={() => setActiveTab("moves")}
                      className="text-sm font-medium text-amber-400 hover:text-amber-300 transition-colors duration-300"
                    >
                      Voir tout
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Profile
